import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { useAuthStore } from '@/store/auth.store';
import { NAV } from './nav';

/*
  The navigation rail.

  Fixed under the command bar and collapsible to icons. Collapsed it is 48px —
  wide enough for a 16px icon in a 32px target — which gives a 1366px laptop
  back 128px of table. That width is why it collapses at all: these screens are
  read on school-office laptops, not on studio monitors.

  The active item is marked three ways on purpose: a 2px left rule, a wash, and
  the primary ink. The rule is what survives collapse, when the label is gone
  and a wash alone on a 48px strip is easy to miss.
*/

export function Sidebar({ open }: { open: boolean }) {
  const role = useAuthStore((s) => s.user?.role);

  return (
    <aside
      aria-label="Main navigation"
      className={cn(
        'fixed left-0 z-40 flex flex-col overflow-y-auto overflow-x-hidden',
        'border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        'transition-[width] duration-200 ease-out',
        'top-bar h-[calc(100vh-theme(spacing.bar))]',
        open ? 'w-rail' : 'w-rail-collapsed',
      )}
    >
      <nav className="flex flex-col gap-0.5 px-1.5 py-2.5">
        {NAV.map((group, gi) => {
          const items = group.items.filter((item) => !item.roles || (role && item.roles.includes(role)));
          if (items.length === 0) return null;

          return (
            <div key={group.label ?? `group-${gi}`} className="flex flex-col gap-0.5">
              {group.label &&
                (open ? (
                  <p className={cn(styles.label, 'mt-2.5 px-2 pb-1')}>{group.label}</p>
                ) : (
                  /* Collapsed, the heading becomes the rule it was implying. */
                  <div className="mx-1.5 my-1.5 border-t border-sidebar-border" aria-hidden />
                ))}

              {items.map(({ label, to, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={open ? undefined : label}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex h-8 items-center rounded-md text-[11.5px] font-semibold',
                      'transition-colors focus-visible:outline-none focus-visible:ring-2',
                      'focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar',
                      open ? 'gap-2.5 px-2' : 'justify-center px-0',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r bg-primary"
                          aria-hidden
                        />
                      )}
                      <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} className="flex-shrink-0" />
                      {open && <span className="truncate">{label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
